import { Link } from "@inertiajs/react"
import { ChevronDown, ChevronRight, ClipboardCheck, Eye, ExternalLink, Pencil, Shield, Trash2 } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { TableCell, TableRow } from "@/components/ui/table"

// One course in the admin track table, plus its lessons when expanded
// (plan 2026-09-23-001): open the course here or in a new tab, and see what it
// holds without leaving the track.

function LessonLine({ course, lesson }) {
  const editUrl = `/app/admin/courses/${course.id}/lessons/${lesson.id}`
  return (
    <li className="flex flex-wrap items-center gap-2 py-1.5">
      <Link href={editUrl} className="text-sm text-zinc-800 hover:text-kf-red hover:underline min-w-0 truncate">
        {lesson.title}
      </Link>
      {lesson.status !== "published" && (
        <Badge variant="secondary" className="text-[10px]">
          Draft
        </Badge>
      )}
      {lesson.coachOnly && (
        <Badge variant="outline" className="text-[10px] gap-1">
          <Shield className="h-3 w-3" />
          Coach only
        </Badge>
      )}
      {lesson.gradedCount > 0 ? (
        <Badge variant="outline" className="text-[10px] gap-1 border-amber-300 text-amber-700 bg-amber-50">
          <ClipboardCheck className="h-3 w-3" />
          Quiz · {lesson.questionCount} questions
        </Badge>
      ) : (
        lesson.questionCount > 0 && <span className="text-[11px] text-zinc-400">{lesson.questionCount} questions</span>
      )}
      <span className="ml-auto flex items-center gap-1">
        <Link href={editUrl} className="p-1.5 text-zinc-400 hover:text-zinc-700" title="Edit lesson">
          <Pencil className="h-3.5 w-3.5" />
        </Link>
        <a
          href={`${editUrl}?view=preview`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1.5 text-zinc-400 hover:text-zinc-700"
          title="Preview lesson in a new tab"
        >
          <Eye className="h-3.5 w-3.5" />
        </a>
      </span>
    </li>
  )
}

export function TrackCourseRows({ course, trackId, expanded, onToggle, onReorder, onRemove, statusBadge }) {
  const courseUrl = `${course.adminUrl}?track=${trackId}`
  const lessons = course.lessons || []
  return [
    <TableRow key={course.trackCourseId} className="border-zinc-100 hover:bg-zinc-50">
      <TableCell className="text-zinc-500">
        <Input
          type="number"
          min="0"
          defaultValue={course.position ?? ""}
          className="w-16 h-8 text-center bg-white border-zinc-200 text-zinc-700"
          onBlur={(e) => {
            const val = parseInt(e.target.value, 10)
            if (!isNaN(val) && val !== course.position) onReorder(course, val)
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.target.blur()
          }}
        />
      </TableCell>
      <TableCell className="font-medium text-zinc-900">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onToggle}
            className="p-1 text-zinc-400 hover:text-zinc-700"
            aria-expanded={expanded}
            title={expanded ? "Hide lessons" : "Show lessons"}
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          <Link href={courseUrl} className="hover:text-kf-red hover:underline">
            {course.title}
          </Link>
          <a
            href={courseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-zinc-400 hover:text-zinc-700"
            title="Open course in a new tab"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <span className="text-xs font-normal text-zinc-400">
            {lessons.length} {lessons.length === 1 ? "lesson" : "lessons"}
          </span>
        </div>
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        {course.category ? (
          <Badge variant="outline" className="text-xs">
            {course.category.name}
          </Badge>
        ) : (
          <span className="text-xs text-zinc-400">--</span>
        )}
      </TableCell>
      <TableCell>{statusBadge}</TableCell>
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
          onClick={() => onRemove(course)}
          title="Remove from track"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>,
    expanded && (
      <TableRow key={`${course.trackCourseId}-lessons`} className="bg-zinc-50 hover:bg-zinc-50">
        <TableCell />
        <TableCell colSpan={4} className="pt-0 pb-3">
          {lessons.length > 0 ? (
            <ol className="divide-y divide-zinc-100 pl-8">
              {lessons.map((lesson) => (
                <LessonLine key={lesson.id} course={course} lesson={lesson} />
              ))}
            </ol>
          ) : (
            <p className="pl-8 text-sm text-zinc-500">
              No lessons yet.{" "}
              <Link href={courseUrl} className="text-kf-red hover:underline">
                Add lessons
              </Link>
            </p>
          )}
        </TableCell>
      </TableRow>
    ),
  ]
}
